import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { MapPin, Package, Wrench, Store, CheckCircle2, Calendar } from "lucide-react";

export default function ShopDetail() {
  const { sid } = useParams();
  const { t, lang } = useLang();
  const { format, convertFromEUR } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shops/${sid}`);
        setData(data);
      } catch (e) { console.warn("Shop load 404 or error:", e?.message); }
      finally { setLoading(false); }
    })();
  }, [sid]);

  if (loading) {
    return <div className="max-w-[1400px] mx-auto p-10 text-center text-[#4B5563]" data-testid="shop-loading">{t("common.loading")}</div>;
  }
  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto p-10 text-center" data-testid="shop-not-found">
        <Store className="w-12 h-12 mx-auto text-[#4B5563] mb-3"/>
        <h1 className="heading font-bold text-2xl">{t("shop.not_found_title")}</h1>
        <p className="text-[#4B5563] mt-2">{t("shop.not_found_desc")}</p>
        <Link to="/boutiques" className="inline-block mt-6 text-[#C85A32] font-semibold hover:underline">← {t("shop.back_to_directory")}</Link>
      </div>
    );
  }

  const { shop, products = [], services = [], owner } = data;
  const isPaused = shop.published === false;
  const total = products.length + services.length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-16" data-testid="shop-detail-page">
      {/* Cover + logo header */}
      <div className="relative rounded-2xl overflow-hidden mb-6 bg-gradient-to-br from-[#1A3322] via-[#24472F] to-[#C85A32]" style={{ minHeight: 220 }}>
        {shop.cover_image && (
          <img src={shop.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" data-testid="shop-detail-cover"/>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent"/>
        <div className="relative flex flex-col sm:flex-row items-start sm:items-end gap-4 p-5 sm:p-8" style={{ minHeight: 220 }}>
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white border-2 border-white shadow-lg flex items-center justify-center overflow-hidden shrink-0" data-testid="shop-detail-logo">
            {shop.logo_url
              ? <img src={shop.logo_url} alt={shop.name} className="w-full h-full object-contain p-1.5"/>
              : <Store className="w-9 h-9 text-[#1A3322]"/>
            }
          </div>
          <div className="text-white flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <h1 className="heading font-extrabold text-2xl sm:text-3xl drop-shadow" data-testid="shop-detail-name">{shop.name}</h1>
              {shop.official && (
                <span className="inline-flex items-center gap-1 bg-white text-[#1A3322] text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3"/>{t("dir.official")}
                </span>
              )}
              {isPaused && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full">
                  ⏸️ {t("shop.paused")}
                </span>
              )}
            </div>
            {shop.location && (
              <p className="text-white/90 text-sm flex items-center gap-1 drop-shadow"><MapPin className="w-3.5 h-3.5"/>{shop.location}</p>
            )}
            {owner && (
              <p className="text-white/80 text-xs mt-1 drop-shadow flex items-center gap-1">
                <Calendar className="w-3 h-3"/>{t("shop.member_since")} {new Date(owner.created_at || Date.now()).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { month: "long", year: "numeric" })}
              </p>
            )}
          </div>
        </div>
      </div>

      {shop.description && (
        <div className="bg-white border border-[#1A3322]/10 rounded-lg p-5 mb-6" data-testid="shop-description">
          <p className="text-[#111812] leading-relaxed whitespace-pre-line">{shop.description}</p>
        </div>
      )}

      {/* Tab switcher */}
      {total === 0 ? (
        <div className="text-center py-16" data-testid="shop-empty">
          <Package className="w-12 h-12 mx-auto text-[#4B5563] mb-3"/>
          <p className="text-[#4B5563]">{t("shop.no_items")}</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 border-b border-[#1A3322]/10 mb-6" data-testid="shop-tabs">
            <button
              onClick={() => setTab("products")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${tab === "products" ? "border-[#C85A32] text-[#1A3322]" : "border-transparent text-[#4B5563] hover:text-[#111812]"}`}
              data-testid="shop-tab-products"
            >
              <Package className="w-4 h-4 inline mr-1.5"/>{t("shop.products")} ({products.length})
            </button>
            <button
              onClick={() => setTab("services")}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${tab === "services" ? "border-[#C85A32] text-[#1A3322]" : "border-transparent text-[#4B5563] hover:text-[#111812]"}`}
              data-testid="shop-tab-services"
            >
              <Wrench className="w-4 h-4 inline mr-1.5"/>{t("shop.services")} ({services.length})
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-grid">
            {(tab === "products" ? products : services).map((item) => (
              <ItemCard key={item.id} item={item} kind={tab === "products" ? "product" : "service"} format={format} convertFromEUR={convertFromEUR} lang={lang} t={t}/>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ItemCard({ item, kind, format, convertFromEUR, lang, t }) {
  const link = `/${kind === "product" ? "produit" : "service"}/${item.id}`;
  const title = lang === "en" && item.title_en ? item.title_en : item.title;
  const price = item.prices?.EUR ?? item.price ?? 0;
  return (
    <Link to={link} className="group bg-white border border-[#1A3322]/10 rounded-lg overflow-hidden hover:border-[#C85A32] hover:shadow-md transition" data-testid={`shop-item-${item.id}`}>
      <div className="aspect-square bg-[#F5F5F0] overflow-hidden">
        {item.images?.[0]
          ? <img src={item.images[0]} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
          : <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-[#4B5563]"/></div>
        }
      </div>
      <div className="p-3">
        <p className="text-sm font-semibold text-[#111812] line-clamp-2 min-h-[2.5rem]">{title}</p>
        <p className="font-bold text-[#C85A32] mt-1">{format(convertFromEUR(price))}</p>
        {item.rating_count > 0 && (
          <p className="text-[11px] text-[#4B5563] mt-0.5">⭐ {(item.rating_avg || 0).toFixed(1)} · {item.rating_count} {t("shop.reviews")}</p>
        )}
      </div>
    </Link>
  );
}
